/**
 * One legend chip (design option B): a rounded pill in the fact's colour with
 * glyphs, stencil role words ("ATK", "DEF", "REACH") and values ("d5", "2").
 * Shared by the ★ legend and the attack popup's dice chip, so the same words
 * appear in both places.
 */
import React from 'react'
import { Text, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import type { ChipSpec } from '../../../lib/ruleOverlayModel'
import type { OverlayPalette } from '../../../lib/rulesOverlayTheme'
import { RuleGlyph } from './RuleGlyph'

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name']

export function RuleChip({ chip, palette, compact, testID }: { chip: ChipSpec; palette: OverlayPalette; compact: boolean; testID?: string }) {
  if (chip.line) {
    // A plain line: icon, then a sentence that wraps across the full width.
    return (
      <View testID={testID} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6, width: '100%' }}>
        {chip.parts.map((part, i) => part.t === 'icon'
          ? <MaterialCommunityIcons key={i} name={part.name as IconName} size={15} color={part.color} style={{ marginTop: 1 }} />
          : part.t === 'label' ? <Text key={i} className="font-typed" style={{ flex: 1, fontSize: compact ? 13 : 14, lineHeight: compact ? 17 : 18, fontWeight: '700', color: palette.ink }}>{part.text}</Text> : null)}
      </View>
    )
  }
  return (
    <View
      testID={testID}
      style={{
        // minHeight + shrink: a long chip wraps its label instead of overflowing a narrow column.
        flexDirection: 'row', alignItems: 'center', gap: 3, minHeight: compact ? 22 : 24, maxWidth: '100%', flexShrink: 1, borderRadius: 999, borderWidth: 1.5,
        borderColor: chip.color, backgroundColor: palette.paper, paddingLeft: chip.bonus ? 8 : 4, paddingRight: chip.bonus ? 8 : 7,
      }}
    >
      {chip.parts.map((part, i) => {
        switch (part.t) {
          case 'text': return <Text key={i} className="font-stencil" style={{ flexShrink: 0, fontSize: 10, color: chip.color }}>{part.text}</Text>
          case 'label': return <Text key={i} className="font-typed" style={{ flexShrink: 1, fontSize: 12, fontWeight: '700', color: part.soft ? palette.inkSoft : chip.color }}>{part.text}</Text>
          case 'glyph': return <RuleGlyph key={i} glyph={part.glyph} color={part.color} size={15} />
          case 'icon': return <MaterialCommunityIcons key={i} name={part.name as IconName} size={14} color={part.color} />
          case 'key': return <Text key={i} className="font-stencil" style={{ flexShrink: 0, fontSize: 8, letterSpacing: 0.3, opacity: 0.85, color: chip.color }}>{part.text}</Text>
          case 'num': return <Text key={i} className="font-stencil" style={{ flexShrink: 0, fontSize: compact ? 11 : 12, color: chip.color }}>{part.text}</Text>
          case 'sep': return <View key={i} style={{ width: 1.5, alignSelf: 'stretch', marginVertical: 4, marginHorizontal: 2, borderRadius: 1, backgroundColor: chip.color, opacity: 0.6 }} />
        }
        return null
      })}
    </View>
  )
}
